import { useEffect, useState } from "react";
import {
  useTimeTrackerStore,
  WorkdaysPerWeek,
} from "../store/timeTrackerStore";
import ClientManager from "./ClientManager";
import DataManagement from "./DataManagement";
import TabModal, { TabDescriptor } from "./TabModal";
import { Button, FormField, Input, Select, Kbd } from "@lazar/ui";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MIN_DAY_HOURS = 0.5;
const MAX_DAY_HOURS = 24;
const MIN_THRESHOLD_MINUTES = 1;
const MAX_THRESHOLD_MINUTES = 30;

const WORKWEEK_OPTIONS: { value: WorkdaysPerWeek; label: string }[] = [
  { value: 4, label: "Mon–Thu (4 days)" },
  { value: 5, label: "Mon–Fri (5 days)" },
  { value: 6, label: "Mon–Sat (6 days)" },
  { value: 7, label: "Mon–Sun (7 days)" },
];

const SettingsModal = ({ isOpen, onClose }: SettingsModalProps) => {
  const {
    settings,
    setDayHours,
    setWorkdaysPerWeek,
    setSmoothingEnabled,
    setSmoothingThreshold,
  } = useTimeTrackerStore();
  const [draft, setDraft] = useState(String(settings.dayHours));
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState(
    String(settings.smoothing.thresholdMinutes),
  );
  const [thresholdError, setThresholdError] = useState<string | null>(null);
  const [thresholdFlash, setThresholdFlash] = useState(false);

  // Re-sync the drafts whenever the modal opens — guards against stale values
  // if the user cancels and reopens after changing the setting elsewhere.
  // TabModal owns resetting the active tab and focus-on-open. SettingsModal
  // stays mounted across open/close (only its TabModal subtree unmounts), so the
  // drafts have to be re-synced from the store on the open transition — a
  // prop/store-driven sync, not something an event handler owns. set-state-in-
  // effect is disabled deliberately; remounting to reset would thrash focus and
  // the open animation.
  useEffect(() => {
    if (isOpen) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setDraft(String(settings.dayHours));
      setError(null);
      setSavedFlash(false);
      setThresholdDraft(String(settings.smoothing.thresholdMinutes));
      setThresholdError(null);
      setThresholdFlash(false);
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [isOpen, settings.dayHours, settings.smoothing.thresholdMinutes]);

  const saveDayHours = () => {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      setError("Enter a number");
      return;
    }
    if (parsed < MIN_DAY_HOURS || parsed > MAX_DAY_HOURS) {
      setError(`Must be between ${MIN_DAY_HOURS} and ${MAX_DAY_HOURS}`);
      return;
    }
    setDayHours(parsed);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const saveThreshold = () => {
    const parsed = Number(thresholdDraft);
    if (!Number.isFinite(parsed)) {
      setThresholdError("Enter a number");
      return;
    }
    if (parsed < MIN_THRESHOLD_MINUTES || parsed > MAX_THRESHOLD_MINUTES) {
      setThresholdError(
        `Must be between ${MIN_THRESHOLD_MINUTES} and ${MAX_THRESHOLD_MINUTES}`,
      );
      return;
    }
    setSmoothingThreshold(parsed);
    setThresholdFlash(true);
    setTimeout(() => setThresholdFlash(false), 1500);
  };

  // Nav order top→bottom reflects frequency of use: Clients is the everyday
  // surface, Data is the rare + destructive "advanced" pane set off below a
  // divider. Only one pane renders at a time, so render order is irrelevant.
  const tabs: TabDescriptor[] = [
    {
      id: "clients",
      label: "Clients",
      render: () => (
        <section aria-labelledby="settings-clients-heading">
          <h4
            id="settings-clients-heading"
            className="text-sm font-black uppercase tracking-wider text-ink dark:text-cream mb-3"
          >
            Clients
          </h4>
          <ClientManager />
        </section>
      ),
    },
    {
      id: "smoothing",
      label: "Smoothing",
      render: () => (
        <section aria-labelledby="settings-smoothing-heading">
          <h4
            id="settings-smoothing-heading"
            className="text-sm font-black uppercase tracking-wider text-ink dark:text-cream mb-3"
          >
            Entry smoothing
          </h4>
          <p className="text-sm text-ink-soft dark:text-cream/70 mb-4">
            Fold a string of short same-client entries into one block when the
            gaps between them are tiny — so a fragmented day reads as continuous
            focus. It's a read-only lens over your real entries: nothing is
            edited, and a gap where you switched to another client is never
            merged. Affects the analytics dashboard and the entries list.
          </p>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.smoothing.enabled}
              onChange={(e) => setSmoothingEnabled(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm text-ink dark:text-cream">
              <span className="font-bold">Enable smoothing</span>{" "}
              <span className="text-ink-soft dark:text-cream/70">
                — off by default; your raw entries are untouched either way.
              </span>
            </span>
          </label>

          <FormField
            className="mt-6"
            label="Merge gaps up to N minutes"
            htmlFor="smoothing-threshold-input"
            error={thresholdError}
            success={thresholdFlash ? "Saved" : undefined}
            hint={
              !thresholdError && !thresholdFlash
                ? "A break at or below this many minutes between two same-client entries counts as still-working and gets absorbed. Default is 3."
                : undefined
            }
          >
            <div className="flex items-start gap-2">
              <Input
                id="smoothing-threshold-input"
                type="number"
                inputMode="numeric"
                min={MIN_THRESHOLD_MINUTES}
                max={MAX_THRESHOLD_MINUTES}
                step={1}
                value={thresholdDraft}
                onChange={(e) => {
                  setThresholdDraft(e.target.value);
                  setThresholdError(null);
                  setThresholdFlash(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveThreshold();
                  }
                }}
                className="flex-1"
              />
              <Button onClick={saveThreshold} variant="primary">
                Apply <Kbd>Enter</Kbd>
              </Button>
            </div>
          </FormField>
        </section>
      ),
    },
    {
      id: "productivity",
      label: "Productivity",
      render: () => (
        <section aria-labelledby="settings-productivity-heading">
          <h4
            id="settings-productivity-heading"
            className="text-sm font-black uppercase tracking-wider text-ink dark:text-cream mb-3"
          >
            Productivity
          </h4>
          <p className="text-sm text-ink-soft dark:text-cream/70 mb-4">
            How many actually-productive hours count as one billable day? Client
            targets are set in days; this rate converts them to hours to compare
            against your tracked time, and analytics show both.
          </p>

          <FormField
            label="1 productive day = N hours"
            htmlFor="day-hours-input"
            error={error}
            success={savedFlash ? "Saved" : undefined}
            hint={
              !error && !savedFlash
                ? "Default is 8 (a standard office day). Many people track 2–5 actually-productive hours per day; calibrate this to match how you bill."
                : undefined
            }
          >
            <div className="flex items-start gap-2">
              <Input
                id="day-hours-input"
                type="number"
                inputMode="decimal"
                min={MIN_DAY_HOURS}
                max={MAX_DAY_HOURS}
                step={0.5}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setError(null);
                  setSavedFlash(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveDayHours();
                  }
                }}
                className="flex-1"
              />
              <Button onClick={saveDayHours} variant="primary">
                Apply <Kbd>Enter</Kbd>
              </Button>
            </div>
          </FormField>

          <FormField
            className="mt-6"
            label="Work week"
            htmlFor="workweek-select"
            hint="Sets the pacing tick in the analytics dashboard. Your weekly day targets stay the same — pacing just spreads them across the number of days you've picked."
          >
            <Select
              id="workweek-select"
              value={settings.workdaysPerWeek}
              onChange={(e) =>
                setWorkdaysPerWeek(Number(e.target.value) as WorkdaysPerWeek)
              }
              className="w-full"
            >
              {WORKWEEK_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormField>
        </section>
      ),
    },
    {
      id: "data",
      label: "Data",
      advanced: true,
      dividerBefore: true,
      render: () => (
        <section aria-labelledby="settings-data-heading">
          <h4
            id="settings-data-heading"
            className="text-sm font-black uppercase tracking-wider text-ink dark:text-cream mb-3"
          >
            Data
          </h4>
          <DataManagement />
        </section>
      ),
    },
  ];

  return (
    <TabModal
      isOpen={isOpen}
      onClose={onClose}
      title="Settings"
      idPrefix="settings"
      tablistLabel="Settings sections"
      closeLabel="Close settings"
      tabs={tabs}
    />
  );
};

export default SettingsModal;
